/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { logoutUser } from "../api/auth";
import { getUserProfile, getPendingApprovalCount } from "../api/api";
import { TRADE_APPROVAL_CHANGED } from "../lib/tradeApprovalEvents";
import PaxiaLogo from "../branding/logo";
import {
  ChevronDown,
  LayoutDashboard,
  Settings,
  Bot,
  History,
  Wallet,
  Menu,
  X,
  Activity,
  TrendingUp,
  BrainCircuit,
  ShieldCheck,
  MessageCircle,
  LogOut,
  User,
  Cloud,
  Cpu,
  FileCode2,
  Play,
  BarChart3,
  ArrowLeft,
  ChevronsLeft,
  ChevronsRight,
  HardDrive,
  BookOpen,
  Wrench,
  Crown,
  CreditCard,
  Sparkles,
  Wand2,
} from "lucide-react";
import { AccountTree as AIWorkflowIcon } from "@mui/icons-material";
import { useSubscriptionAccess } from "../context/SubscriptionAccessContext";
import { AI_ROUTE_SURFACES, APP_ROUTE_SURFACES } from "../access/surfaces";


const normalizeSidebarIcon = (icon) => {
  if (!React.isValidElement(icon)) {
    return icon;
  }

  return React.cloneElement(icon, {
    className: `h-5 w-5 shrink-0 ${icon.props.className || ""}`.trim(),
  });
};


const NavItem = ({
  to,
  icon,
  children,
  onClick,
  end,
  collapsed,
  matchPattern,
  badge,
  variant = "default",
}) => {
  const location = useLocation();
  const normalizedIcon = normalizeSidebarIcon(icon);

  const variantStyles = {
    default: {
      base: "",
      inactive: "hover:bg-primary/20",
      active: "bg-primary text-textAccent",
    },
  };

  const styles = variantStyles[variant] || variantStyles.default;
  const baseItemClasses = "group flex items-center rounded-lg transition-colors duration-200";
  const sizeClasses = collapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2.5";

  return (
    <li>
      <NavLink
        to={to}
        end={end}
        onClick={onClick}
        title={collapsed ? children : undefined}
        className={({ isActive }) => {
          const extraMatch = matchPattern ? matchPattern.test(location.pathname) : false;
          const stateClasses = (isActive || extraMatch) ? styles.active : styles.inactive;
          return `${baseItemClasses} ${sizeClasses} ${styles.base} ${stateClasses}`;
        }}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-current">{normalizedIcon}</span>
        {!collapsed && <span className="flex-1 text-sm">{children}</span>}
        {badge > 0 && (
          <span className="ml-auto min-w-[1.25rem] h-5 flex items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white px-1">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </NavLink>
    </li>
  );
};

const AccordionItem = ({ icon, title, children, collapsed, variant = "default", isActive = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const normalizedIcon = normalizeSidebarIcon(icon);

  useEffect(() => {
    if (isActive) {
      setIsOpen(true);
    }
  }, [isActive]);

  const isAutomationVariant = variant === "automation";
  const buttonStateClasses = isAutomationVariant
    ? `${isOpen || isActive ? 'bg-primary/10' : 'opacity-80'} hover:bg-primary/20`
    : "hover:bg-primary/20";
  const buttonBaseClasses = "";

  if (collapsed) {
    return (
      <li>
        <button
          onClick={() => setIsOpen(!isOpen)}
          title={title}
          className="flex items-center justify-center w-full p-2.5 rounded-lg text-primary transition hover:bg-primary/15 focus:outline-none focus-visible:bg-primary/15"
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-current">{normalizedIcon}</span>
        </button>
      </li>
    );
  }

  return (
    <li>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full p-3 rounded-lg text-left text-primary transition focus:outline-none focus-visible:bg-primary/15 ${isOpen ? 'bg-primary/10' : 'hover:bg-primary/15'}`}
      >
        <div className="flex items-center">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-current">{normalizedIcon}</span>
          <span className="ml-3 text-sm">{title}</span>
        </div>
        <ChevronDown
          className={`h-4 w-4 opacity-70 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && <ul className={`mt-1 space-y-1 ${isAutomationVariant ? 'pl-3' : ''}`}>{children}</ul>}
    </li>
  );
};

const SidebarCollapsibleSection = ({ title, collapsed, isActive = false, children }) => {
  const [isOpen, setIsOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) {
      setIsOpen(true);
    }
  }, [isActive]);

  if (collapsed) {
    return <>{children}</>;
  }

  const buttonClassName = [
    'flex w-full items-center justify-between rounded-lg p-3 text-left transition',
    'focus:outline-none focus-visible:bg-primary/15 focus-visible:text-primary',
    isActive
      ? 'bg-primary/10 text-primary'
      : isOpen
        ? 'bg-primary/[0.06] text-primary'
        : 'text-primary hover:bg-primary/15',
  ].join(' ');

  return (
    <>
      <li>
        <button
          type="button"
          onClick={() => setIsOpen((value) => !value)}
          className={buttonClassName}
          aria-expanded={isOpen}
        >
          <span className="font-medium text-sm text-current">
            {title}
          </span>
          <ChevronDown className={`w-5 h-5 text-current opacity-75 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </li>
      {isOpen ? children : null}
    </>
  );
};

const FooterMenuLink = ({ to, icon, children, onClick, end }) => {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) => [
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition",
        isActive ? "bg-primary text-textAccent" : "text-textPrimary hover:bg-primary/20",
      ].join(" ")}
    >
      <span className="flex h-4 w-4 items-center justify-center text-current">{icon}</span>
      <span className="truncate">{children}</span>
    </NavLink>
  );
};

const SidebarFooterMenu = ({ collapsed, isMobile, userProfile, onLogout, onNavigate }) => {
  const location = useLocation();
  const containerRef = useRef(null);
  const [isPinnedOpen, setIsPinnedOpen] = useState(false);
  const [isHoverOpen, setIsHoverOpen] = useState(false);

  const closeMenu = useCallback(() => {
    setIsPinnedOpen(false);
    setIsHoverOpen(false);
  }, []);

  const isMenuOpen = isPinnedOpen || isHoverOpen;
  const isAccountRoute = ["/profile", "/subscription", "/billing"].includes(location.pathname);
  const displayName = userProfile?.name ? `${userProfile.name} ${userProfile.surname || ""}`.trim() : "User";
  const initials = userProfile?.name
    ? `${userProfile.name.charAt(0)}${userProfile.surname?.charAt(0) || ""}`
    : null;

  useEffect(() => {
    closeMenu();
  }, [location.pathname, collapsed, isMobile, closeMenu]);

  useEffect(() => {
    if (!isPinnedOpen) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isPinnedOpen, closeMenu]);

  const handleTriggerClick = () => {
    if (isPinnedOpen) {
      closeMenu();
      return;
    }
    setIsPinnedOpen(true);
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHoverOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHoverOpen(false);
    }
  };

  const handleMenuNavigate = () => {
    closeMenu();
    onNavigate?.();
  };

  const handleMenuLogout = () => {
    closeMenu();
    onNavigate?.();
    onLogout();
  };

  const triggerClasses = [
    "flex w-full items-center rounded-xl border border-white/10 bg-white/[0.03] text-left transition",
    collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5",
    isMenuOpen || isAccountRoute ? "bg-primary/10 border-primary/30" : "hover:bg-primary/20",
  ].join(" ");

  const menuClasses = [
    "absolute bottom-full z-20 mb-2 rounded-xl border border-white/10 bg-sidebar/95 p-2 shadow-2xl backdrop-blur",
    collapsed ? "left-0 w-64" : "left-0 right-0",
  ].join(" ");

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isMenuOpen ? (
        <div className={menuClasses}>
          <div className="px-3 pb-2 pt-1">
            <p className="text-xs font-semibold text-textPrimary truncate">{displayName}</p>
            {userProfile?.email ? (
              <p className="mt-0.5 text-[11px] text-textSecondary truncate">{userProfile.email}</p>
            ) : null}
          </div>
          <div className="space-y-1">
            <FooterMenuLink to="/profile" icon={<User className="h-4 w-4" />} onClick={handleMenuNavigate}>
              Profile
            </FooterMenuLink>
            <FooterMenuLink to="/subscription" icon={<Crown className="h-4 w-4" />} onClick={handleMenuNavigate}>
              Subscription
            </FooterMenuLink>
            <FooterMenuLink to="/billing" icon={<CreditCard className="h-4 w-4" />} onClick={handleMenuNavigate}>
              Billing
            </FooterMenuLink>
            <div className="my-2 border-t border-white/10" />
            <button
              type="button"
              onClick={handleMenuLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-textPrimary transition hover:bg-primary/20"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={handleTriggerClick}
        className={triggerClasses}
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        title={collapsed ? displayName : undefined}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-backgroundBody/60">
          {initials ? (
            <span className="text-[10px] font-bold uppercase leading-none">{initials}</span>
          ) : (
            <User className="h-4 w-4" />
          )}
        </div>
        {!collapsed ? (
          <>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-textPrimary truncate">{displayName}</p>
              {userProfile?.email ? (
                <p className="text-[11px] text-textSecondary truncate">{userProfile.email}</p>
              ) : null}
            </div>
            <ChevronDown className={`h-4 w-4 shrink-0 text-textSecondary transition-transform ${isMenuOpen ? "rotate-180" : ""}`} />
          </>
        ) : null}
      </button>
    </div>
  );
};

const Sidebar = ({ isOpen = true, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessSnapshot, canAccessSurface } = useSubscriptionAccess();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  // Fetch user profile for sidebar footer
  useEffect(() => {
    const loadUserProfile = () => {
      getUserProfile()
        .then((data) => setUserProfile(data))
        .catch(() => {});
    };

    loadUserProfile();
    window.addEventListener("paxia:profile-updated", loadUserProfile);

    return () => window.removeEventListener("paxia:profile-updated", loadUserProfile);
  }, []);

  // Fetch pending trade approval count (poll every 30s + instant on WS notification)
  const fetchApprovalCount = useCallback(() => {
    getPendingApprovalCount()
      .then((data) => setPendingApprovalCount(data?.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchApprovalCount();
    const interval = setInterval(fetchApprovalCount, 30000);
    return () => clearInterval(interval);
  }, [fetchApprovalCount]);

  // Instant refresh when any approval change occurs (WS notification or user action)
  useEffect(() => {
    const handler = () => fetchApprovalCount();
    window.addEventListener(TRADE_APPROVAL_CHANGED, handler);
    return () => window.removeEventListener(TRADE_APPROVAL_CHANGED, handler);
  }, [fetchApprovalCount]);

  // Derive admin status from the auth user stored in localStorage (populated by /accounts/me/)
  const isAdmin = accessSnapshot?.user?.is_staff ?? (() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed?.is_staff === true;
      } catch {
        return userProfile?.is_staff === true;
      }
    }
    return userProfile?.is_staff === true;
  })();

  const canViewDashboard = canAccessSurface(APP_ROUTE_SURFACES.dashboard).allowed;
  const canViewStudio = canAccessSurface(APP_ROUTE_SURFACES.studio).allowed;
  const canViewAutomation = canAccessSurface(APP_ROUTE_SURFACES.ai).allowed;
  const canViewHistory = canAccessSurface(APP_ROUTE_SURFACES.selectAccountForHistory).allowed;
  const canViewResearch = canAccessSurface(APP_ROUTE_SURFACES.researchHub).allowed;
  const canViewApprovals = canAccessSurface(APP_ROUTE_SURFACES.approvals).allowed;
  const canViewAccounts = canAccessSurface(APP_ROUTE_SURFACES.accounts).allowed;
  const canViewSettings = canAccessSurface(APP_ROUTE_SURFACES.settings).allowed;
  const canViewAISystems = canAccessSurface(AI_ROUTE_SURFACES.systems).allowed;
  const canViewBots = canAccessSurface(APP_ROUTE_SURFACES.botsList).allowed;
  const canViewAIAnalytics = canAccessSurface(AI_ROUTE_SURFACES.analytics).allowed;
  const canViewAIAgents = canAccessSurface(AI_ROUTE_SURFACES.agents).allowed;
  const canViewAISnippets = canAccessSurface(AI_ROUTE_SURFACES.snippets).allowed;
  const canViewAIIndicatorSets = canAccessSurface(AI_ROUTE_SURFACES.indicatorSets).allowed;
  const canViewAIGlobalStorage = canAccessSurface(AI_ROUTE_SURFACES.globalStorage).allowed;
  const canViewAIExperts = canAccessSurface(AI_ROUTE_SURFACES.experts).allowed;
  const canViewAIModelLab = canAccessSurface(AI_ROUTE_SURFACES.mlOverview).allowed;
  const canViewAIProviders = canAccessSurface(AI_ROUTE_SURFACES.providers).allowed;
  const canViewAIModels = canAccessSurface(AI_ROUTE_SURFACES.models).allowed;
  const canViewAIEmbeddingModels = canAccessSurface(AI_ROUTE_SURFACES.embeddingModels).allowed;
  const canViewAIServiceInstructions = canAccessSurface(AI_ROUTE_SURFACES.serviceInstructions).allowed;
  const showAutomationAdminSection = [
    canViewAIModelLab,
    canViewAIProviders,
    canViewAIModels,
    canViewAIEmbeddingModels,
    canViewAIServiceInstructions,
  ].some(Boolean);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logoutUser();
    navigate("/login");
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const collapsed = !isOpen;
  const isAutomationSection = location.pathname.startsWith('/ai') || location.pathname.startsWith('/bots');
  const isAutomationBuildSection = location.pathname.startsWith('/ai/systems') || location.pathname.startsWith('/bots') || location.pathname.startsWith('/ai/schedules');
  const isAutomationMonitorSection = location.pathname.startsWith('/ai/analytics') || location.pathname.startsWith('/ai/executions');
  const isAutomationConfigureSection = location.pathname.startsWith('/ai/agents') || location.pathname.startsWith('/ai/snippets') || location.pathname.startsWith('/ai/indicator-sets') || location.pathname.startsWith('/ai/global-storage');
  const isAutomationAdvancedSection = location.pathname.startsWith('/ai/experts');
  const isAutomationAdminSection = location.pathname.startsWith('/ai/ml') || location.pathname.startsWith('/ai/providers') || location.pathname.startsWith('/ai/models') || location.pathname.startsWith('/ai/service-instructions');
  const isAutomationModelLabSection = location.pathname.startsWith('/ai/ml');

  const sidebarContent = (isMobile = false) => (
    <>
      <div className="flex-shrink-0">
        <div className={`mb-4 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <img src={PaxiaLogo} alt="paxia.ai" className="h-8 w-auto object-contain bg-transparent select-none" />
          )}
          {isMobile ? (
            <button
              onClick={closeMobileMenu}
              className="p-1.5 rounded-lg hover:bg-primary/20 transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={onToggle}
              className={`p-1.5 rounded-lg hover:bg-primary/20 transition-colors ${collapsed ? '' : 'ml-auto'}`}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 overscroll-contain sidebar-nav-scroll">
        <ul className={collapsed ? "space-y-1" : isAutomationSection ? "space-y-2" : "space-y-3"}>
          {isAutomationSection ? (
            <>
              {/* Back to trading dashboard */}
              <li>
                <NavLink
                  to="/dashboard"
                  onClick={closeMobileMenu}
                  title={collapsed ? "Back to Trading" : undefined}
                  className={`flex items-center rounded-lg text-sm opacity-80 transition-colors duration-200 hover:bg-primary/20 ${collapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'}`}
                >
                  <ArrowLeft className="w-5 h-5" />
                  {!collapsed && <span>Back to Trading</span>}
                </NavLink>
              </li>
              <li>
                <hr
                  className="h-px border-0"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary) 32%, transparent)' }}
                />
              </li>
              <NavItem to="/ai" end icon={<LayoutDashboard />} onClick={closeMobileMenu} collapsed={collapsed}>
                Automation Hub
              </NavItem>
              <SidebarCollapsibleSection title="Automate" collapsed={collapsed} isActive={isAutomationBuildSection}>
                {canViewAISystems && (
                <NavItem to="/ai/systems" icon={<BrainCircuit />} onClick={closeMobileMenu} collapsed={collapsed}>
                  AI Systems
                </NavItem>
                )}
                {canViewBots && (
                <NavItem to="/bots" icon={<Bot />} onClick={closeMobileMenu} collapsed={collapsed}>
                  Trading Bots
                </NavItem>
                )}
              </SidebarCollapsibleSection>
              <SidebarCollapsibleSection title="Monitor" collapsed={collapsed} isActive={isAutomationMonitorSection}>
                {canViewAIAnalytics && (
                <NavItem to="/ai/analytics?tab=runs" icon={<BarChart3 />} onClick={closeMobileMenu} collapsed={collapsed}>
                  Insights
                </NavItem>
                )}
              </SidebarCollapsibleSection>
              <SidebarCollapsibleSection title="Configure" collapsed={collapsed} isActive={isAutomationConfigureSection}>
                {canViewAIAgents && (
                <NavItem to="/ai/agents" icon={<Wand2 />} onClick={closeMobileMenu} collapsed={collapsed}>
                  Agents
                </NavItem>
                )}
                {canViewAISnippets && (
                <NavItem to="/ai/snippets" icon={<FileCode2 />} onClick={closeMobileMenu} collapsed={collapsed}>
                  Prompt Snippets
                </NavItem>
                )}
                {canViewAIIndicatorSets && (
                <NavItem to="/ai/indicator-sets" icon={<TrendingUp />} onClick={closeMobileMenu} collapsed={collapsed}>
                  Indicator Presets
                </NavItem>
                )}
                {canViewAIGlobalStorage && (
                <NavItem to="/ai/global-storage" icon={<HardDrive />} onClick={closeMobileMenu} collapsed={collapsed}>
                  Global Storage
                </NavItem>
                )}
              </SidebarCollapsibleSection>
              <SidebarCollapsibleSection title="Advanced" collapsed={collapsed} isActive={isAutomationAdvancedSection}>
                {canViewAIExperts && (
                <NavItem to="/ai/experts" icon={<User />} onClick={closeMobileMenu} collapsed={collapsed}>
                  Expert Library
                </NavItem>
                )}
              </SidebarCollapsibleSection>
              {isAdmin && showAutomationAdminSection && (
                <SidebarCollapsibleSection title="Admin" collapsed={collapsed} isActive={isAutomationAdminSection}>
                  {canViewAIModelLab && (
                  <AccordionItem icon={<BrainCircuit />} title="Model Lab" collapsed={collapsed}>
                    <NavItem to="/ai/ml" end icon={<LayoutDashboard />} onClick={closeMobileMenu}>
                      Overview
                    </NavItem>
                    <NavItem to="/ai/ml/datasets" icon={<HardDrive />} onClick={closeMobileMenu}>
                      Datasets
                    </NavItem>
                    <NavItem to="/ai/ml/features" icon={<TrendingUp />} onClick={closeMobileMenu}>
                      Features
                    </NavItem>
                    <NavItem to="/ai/ml/models" icon={<Bot />} onClick={closeMobileMenu}>
                      Registry
                    </NavItem>
                    <NavItem to="/ai/ml/training-jobs" icon={<Play />} onClick={closeMobileMenu}>
                      Training Jobs
                    </NavItem>
                    <NavItem to="/ai/ml/experiments" icon={<BarChart3 />} onClick={closeMobileMenu}>
                      Experiments
                    </NavItem>
                    <NavItem to="/ai/ml/deployments" icon={<Wand2 />} onClick={closeMobileMenu}>
                      Deployments
                    </NavItem>
                  </AccordionItem>
                  )}
                  {canViewAIProviders && (
                  <NavItem to="/ai/providers" icon={<Cloud />} onClick={closeMobileMenu} collapsed={collapsed}>
                    LLM Providers
                  </NavItem>
                  )}
                  {canViewAIModels && (
                  <NavItem to="/ai/models" icon={<Cpu />} onClick={closeMobileMenu} collapsed={collapsed}>
                    LLM Models
                  </NavItem>
                  )}
                  {canViewAIEmbeddingModels && (
                  <NavItem to="/ai/embedding-models" icon={<Sparkles />} onClick={closeMobileMenu} collapsed={collapsed}>
                    Embedding Models
                  </NavItem>
                  )}
                  {canViewAIServiceInstructions && (
                  <NavItem to="/ai/service-instructions" icon={<BookOpen />} onClick={closeMobileMenu} collapsed={collapsed}>
                    Service Instructions
                  </NavItem>
                  )}
                </SidebarCollapsibleSection>
              )}
            </>
          ) : (
            <>
            {canViewDashboard && (
            <NavItem to="/dashboard" icon={<LayoutDashboard />} onClick={closeMobileMenu} collapsed={collapsed}>
              Dashboard
            </NavItem>
            )}
            {canViewStudio && (
            <NavItem to="/studio" icon={<Activity />} onClick={closeMobileMenu} collapsed={collapsed}>
              Studio
            </NavItem>
            )}
            {/* Automation - integrated within the same app */}
            {canViewAutomation && (
            <NavItem to="/ai" icon={<Sparkles />} onClick={closeMobileMenu} collapsed={collapsed}>
              Automation
            </NavItem>
            )}
            {canViewHistory && (
            <NavItem to="/history" icon={<History />} onClick={closeMobileMenu} collapsed={collapsed} matchPattern={/^\/accounts\/[^/]+\/trade-history|^\/trades\/history\//}>
              Trade History
            </NavItem>
            )}
            {canViewResearch && (
            <NavItem to="/research" icon={<BookOpen />} onClick={closeMobileMenu} collapsed={collapsed} matchPattern={/^\/research$|^\/market$|^\/screener$|^\/explorer\/|^\/fundamentals\/|^\/analysis(?:\/|$)|^\/win-rate(?:\/|$)|^\/alerts(?:\/|$)/}>
              Research Hub
            </NavItem>
            )}
            {canViewApprovals && (
            <NavItem to="/approvals" icon={<ShieldCheck />} onClick={closeMobileMenu} collapsed={collapsed} matchPattern={/^\/approvals/} badge={pendingApprovalCount}>
              Trade Approvals
            </NavItem>
            )}
            {canViewAccounts && (
            <NavItem to="/accounts" end icon={<Wallet />} onClick={closeMobileMenu} collapsed={collapsed} matchPattern={/^\/accounts(?:$|\/(?:legacy|preview)(?:\/|$))/}>
              Broker Accounts
            </NavItem>
            )}
            {canViewSettings && (
            <NavItem
              to="/settings"
              icon={<Settings />}
              onClick={closeMobileMenu}
              collapsed={collapsed}
              matchPattern={/^\/settings(?:\/|$)|^\/platform-admin(?:\/|$)/}
            >
              Settings
            </NavItem>
            )}
            </>
          )}
        </ul>
      </nav>
      <div className={`flex-shrink-0 pt-2 pb-0 space-y-0.5 border-t border-primary/20 mt-auto ${collapsed ? 'items-center' : ''}`}>
        <SidebarFooterMenu
          collapsed={collapsed}
          isMobile={isMobile}
          userProfile={userProfile}
          onLogout={handleLogout}
          onNavigate={closeMobileMenu}
        />
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Hamburger Menu Button - only visible when sidebar is closed */}
      {!isMobileMenuOpen && (
        <div className="md:hidden fixed top-2 left-2 z-[60]">
          <button 
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-1.5 sm:p-2 rounded-lg bg-sidebar text-sidebarText hover:opacity-90 transition-colors shadow-lg"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[45] md:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed top-0 bottom-0 left-0 w-72 max-w-[85vw] bg-sidebar text-sidebarText p-4 transform ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out md:hidden z-[50] flex flex-col`}
        style={{
          paddingBottom: '2rem',
        }}
      >
        {sidebarContent(true)}
      </aside>

      {/* Desktop Sidebar — always visible, expands/collapses */}
      <aside
        className="hidden md:flex fixed inset-y-0 left-0 bg-sidebar text-sidebarText pb-6 flex-col border-r border-borderDefault transition-all duration-300 ease-in-out z-40 box-border"
        style={{
          width: isOpen ? 'var(--sidebar-width)' : 'var(--sidebar-width-collapsed)',
          padding: isOpen ? '1rem' : '0.75rem 0.5rem 1.5rem',
          borderBottomRightRadius: 'var(--radius-xl)',
        }}
      >
        {sidebarContent(false)}
      </aside>
    </>
  );
};

export default Sidebar;
